import { db, auth } from "../../firebase/config";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  doc,
  getDoc
} from "firebase/firestore";
import {
  resolveChatParticipantPair,
  canonicalPrivateLandlordUid
} from "../../utils/firestoreQueries";

/**
 * Creates a new chat or returns existing chat between tenant and landlord
 * @param tenantUid - The tenant's user ID
 * @param landlordUid - The landlord's user ID  
 * @param propertyId - Optional property ID to associate with the chat
 * @returns Promise<{ id: string }> - The chat document ID
 */
export async function createOrGetChat(tenantUid, landlordUid, propertyId) {
  if (!tenantUid || !landlordUid) {
    throw new Error("Both tenantUid and landlordUid are required");
  }

  console.log(`Creating or getting chat between tenant: ${tenantUid} and landlord: ${landlordUid}, property: ${propertyId || 'none'}`);

  try {
    let propertyPayload = null;
    if (propertyId) {
      const propertyDoc = await getDoc(doc(db, "koten", propertyId));
      if (propertyDoc.exists()) {
        propertyPayload = { id: propertyDoc.id, ...propertyDoc.data() };
      }
    }

    const pair = resolveChatParticipantPair(propertyPayload, tenantUid, landlordUid);

    const chatsRef = collection(db, "chats");
    
    // Query must use the current user's UID in array-contains so Firestore
    // security rules can verify the caller is a participant of every result.
    const currentUid = auth.currentUser?.uid;
    const queryUid = currentUid && (currentUid === tenantUid || currentUid === landlordUid)
      ? currentUid
      : landlordUid;

    const existingChatQuery = query(
      chatsRef,
      where("participants", "array-contains", queryUid)
    );
    
    const existingChatsSnapshot = await getDocs(existingChatQuery);
    
  // Check if any existing chat has both participants
  // If propertyId is provided, return only the chat for that property
    for (const doc of existingChatsSnapshot.docs) {
      const chatData = doc.data();
      const participants = chatData.participants || [];
      
      // Check if this chat has both tenant and landlord
    if (participants.includes(pair.tenantUid) && participants.includes(pair.landlordUid)) {
      if (propertyId) {
        if (chatData.propertyId === propertyId) {
          console.log(`Found existing chat for property ${propertyId}: ${doc.id}`);
          return { id: doc.id };
        }
        continue;
      }
      console.log(`Found existing chat between tenant and landlord: ${doc.id}`);
      return { id: doc.id };
    }
    }

    // No existing chat found, create a new one
    console.log("No existing chat found, creating new chat");
    
    const newChatData = {
      participants: pair.participants,
      tenantId: pair.tenantUid,
      landlordId: pair.landlordUid,
      ownerId: canonicalPrivateLandlordUid(propertyPayload) || pair.landlordUid,
      agencyId: propertyPayload?.management?.agencyId || propertyPayload?.createdByAgencyId || null,
      clientLinkId: propertyPayload?.management?.clientLinkId || null,
      propertyId: propertyId || null,
      isActive: true,
      lastMessage: null,
      lastMessageAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(chatsRef, newChatData);
    console.log(`New chat created with ID: ${docRef.id}`);
    
    return { id: docRef.id };
    
  } catch (error) {
    console.error("Error creating or getting chat:", error);
    throw new Error(`Failed to create or get chat: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Helper function to get chat by ID
 * @param chatId - The chat document ID
 * @returns Promise<any> - The chat document data
 */
export async function getChatById(chatId) {
  if (!chatId) {
    throw new Error("Chat ID is required");
  }

  try {
    const chatRef = doc(db, "chats", chatId);
    const chatDoc = await getDoc(chatRef);
    
    if (!chatDoc.exists()) {
      throw new Error("Chat not found");
    }
    
    return { id: chatDoc.id, ...chatDoc.data() };
  } catch (error) {
    console.error("Error getting chat by ID:", error);
    throw new Error(`Failed to get chat: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
